import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import type {
  CashReportCycleDetailsDto,
  CashReportCycleListItem,
} from '../../Types/cashReportTypes';
import {
  useCreateCashReportCycleMutation,
  useGetCashReportCycleDetailsQuery,
  useGetCashReportCyclesQuery,
} from '../../Service/extendedCashDocumentsApi';
import { useGetCashReportCompaniesLookupQuery } from '../../Service/cashReportCompaniesLookupApi';
import type { CashReportCompanyLookup } from '../../Service/cashReportCompaniesLookupMapper';
import { formatAmount } from '../../Types/cashDocumentTypes';
import { toCashReportsUserMessage } from '../../Utils/cashReportsErrorMessage';
import CashReportPdfActions from '../CashReportPdfActions/CashReportPdfActions';

const DISPLAY_DATE_FORMAT = 'DD.MM.YYYY';
const formatDisplayDate = (dateValue: string): string => {
  const parsedDate = dayjs(dateValue);
  return parsedDate.isValid() ? parsedDate.format(DISPLAY_DATE_FORMAT) : dateValue;
};
const formatRange = (cycle: CashReportCycleListItem): string =>
  `${formatDisplayDate(cycle.dateFrom)} - ${formatDisplayDate(cycle.dateTo)}`;
const CYCLE_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const CYCLE_MONTH_MIN_YEAR = 2000;
const CYCLE_MONTH_MAX_YEAR = 2100;
const CYCLE_MONTH_ERROR_MESSAGE = `Podaj miesiąc cyklu w formacie YYYY-MM i zakresie lat ${CYCLE_MONTH_MIN_YEAR}-${CYCLE_MONTH_MAX_YEAR}.`;
const OPEN_CYCLE_STATUS = 'open';
const CLOSED_CYCLE_STATUS = 'closed';

type CycleStatusChip = {
  label: string;
  color: 'success' | 'default';
};

const toCycleStatusChip = (status: string): CycleStatusChip => {
  if (status === OPEN_CYCLE_STATUS) {
    return { label: 'Otwarty', color: 'success' };
  }

  if (status === CLOSED_CYCLE_STATUS) {
    return { label: 'Zamknięty', color: 'default' };
  }

  return { label: status, color: 'default' };
};

const isCycleMonthWithinRange = (monthValue: string): boolean => {
  if (!CYCLE_MONTH_PATTERN.test(monthValue)) {
    return false;
  }

  const [yearPart] = monthValue.split('-');
  const year = Number(yearPart);

  if (!Number.isInteger(year)) {
    return false;
  }

  return year >= CYCLE_MONTH_MIN_YEAR && year <= CYCLE_MONTH_MAX_YEAR;
};

const CashReportsList: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<CashReportCompanyLookup | null>(null);
  const [cycleMonth, setCycleMonth] = useState<string>(dayjs().format('YYYY-MM'));
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const pendingCreatedCycleIdRef = useRef<number | null>(null);
  const { companies, isLoading: isCompaniesLoading } = useGetCashReportCompaniesLookupQuery();
  const companyNameById = useMemo(
    () => new Map<number, string>(companies.map((company) => [company.id, company.name])),
    [companies]
  );

  const {
    data: cycles = [],
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
  } = useGetCashReportCyclesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [createCycle, { isLoading: isCreating }] = useCreateCashReportCycleMutation();

  const {
    data: cycleDetails,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: detailsError,
  } = useGetCashReportCycleDetailsQuery(selectedCycleId ?? 0, {
    skip: selectedCycleId === null,
  });

  useEffect(() => {
    if (selectedCycleId === null) {
      if (cycles.length > 0) {
        setSelectedCycleId(cycles[0].cycleId);
      }
      return;
    }

    if (cycles.some((cycle) => cycle.cycleId === selectedCycleId)) {
      if (pendingCreatedCycleIdRef.current === selectedCycleId) {
        pendingCreatedCycleIdRef.current = null;
      }
      return;
    }

    if (pendingCreatedCycleIdRef.current === selectedCycleId) {
      return;
    }

    pendingCreatedCycleIdRef.current = null;

    if (cycles.length === 0) {
      setSelectedCycleId(null);
      return;
    }

    if (cycles[0].cycleId !== selectedCycleId) {
      setSelectedCycleId(cycles[0].cycleId);
    }
  }, [cycles, selectedCycleId]);

  const toUserMessage = (error: unknown, fallbackMessage: string): string => {
    return toCashReportsUserMessage(error, fallbackMessage);
  };

  const handleCreateCycle = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);

    if (!selectedCompany) {
      setCreateError('Wybierz firmę z listy.');
      return;
    }

    if (!isCycleMonthWithinRange(cycleMonth)) {
      setCreateError(CYCLE_MONTH_ERROR_MESSAGE);
      return;
    }

    try {
      const created = await createCycle({
        companyId: selectedCompany.id,
        cycleMonth,
      }).unwrap();
      pendingCreatedCycleIdRef.current = created.cycleId;
      setSelectedCycleId(created.cycleId);
      setCreateSuccess(`Cykl raportu ${created.cycleMonth} został utworzony.`);
    } catch (error) {
      setCreateError(toUserMessage(error, 'Nie udało się utworzyć cyklu raportu kasowego.'));
    }
  };

  const renderDetails = (details: CashReportCycleDetailsDto) => {
    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Firma
            </Typography>
            <Typography variant="body1">
              {details.companyName || `ID ${details.companyId}`}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Miesiąc
            </Typography>
            <Typography variant="body1">{details.cycleMonth}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Zakres
            </Typography>
            <Typography variant="body1">
              {formatDisplayDate(details.dateFrom)} - {formatDisplayDate(details.dateTo)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Dokumenty
            </Typography>
            <Typography variant="body1">{details.documentsCount}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Suma KP
            </Typography>
            <Typography variant="body1" color="success.main">
              {formatAmount(details.totalKpAmount, 'PLN')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Suma KW
            </Typography>
            <Typography variant="body1" color="error.main">
              {formatAmount(details.totalKwAmount, 'PLN')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Saldo
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formatAmount(details.balance, 'PLN')}
            </Typography>
          </Grid>
        </Grid>

        <CashReportPdfActions cycleId={details.cycleId} cycleMonth={details.cycleMonth} />
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <Box component="form" onSubmit={handleCreateCycle}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <Autocomplete
              fullWidth
              options={companies}
              loading={isCompaniesLoading}
              value={selectedCompany}
              onChange={(_, nextCompany) => setSelectedCompany(nextCompany)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} required size="small" label="Firma" />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              required
              size="small"
              type="month"
              label="Miesiąc cyklu"
              value={cycleMonth}
              onChange={(event) => setCycleMonth(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={isCreating}
            >
              Utwórz cykl
            </Button>
          </Grid>
        </Grid>
      </Box>

      {createSuccess && <Alert severity="success">{createSuccess}</Alert>}
      {createError && <Alert severity="error">{createError}</Alert>}

      {isListLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {isListError && (
        <Alert severity="error">
          {toUserMessage(listError, 'Nie udało się pobrać listy cykli raportu kasowego.')}
        </Alert>
      )}

      {!isListLoading && !isListError && cycles.length === 0 && (
        <Alert severity="info">Brak cykli raportu kasowego.</Alert>
      )}

      {!isListLoading && !isListError && cycles.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Miesiąc</TableCell>
                <TableCell>Firma</TableCell>
                <TableCell>Zakres</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cycles.map((cycle) => {
                const cycleStatusChip = toCycleStatusChip(cycle.status);
                const companyLabel = companyNameById.get(cycle.companyId) ?? `ID ${cycle.companyId}`;

                return (
                  <TableRow
                    key={cycle.cycleId}
                    selected={selectedCycleId === cycle.cycleId}
                  >
                    <TableCell>{cycle.cycleMonth}</TableCell>
                    <TableCell>{companyLabel}</TableCell>
                    <TableCell>{formatRange(cycle)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={cycleStatusChip.label} color={cycleStatusChip.color} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => setSelectedCycleId(cycle.cycleId)}
                      >
                        Szczegóły
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="h6">Szczegóły cyklu</Typography>

      {selectedCycleId !== null && isDetailsLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedCycleId !== null && isDetailsError && (
        <Alert severity="error">
          {toUserMessage(detailsError, 'Nie udało się pobrać szczegółów cyklu raportu kasowego.')}
        </Alert>
      )}

      {selectedCycleId !== null && !isDetailsLoading && !isDetailsError && cycleDetails && (
        <Paper sx={{ p: 2 }}>{renderDetails(cycleDetails)}</Paper>
      )}
    </Stack>
  );
};

export default CashReportsList;
